import CloseIcon from '@mui/icons-material/CloseRounded';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import {
  Button as SharedButton,
  LabeledSwitcher,
  Loader,
  UsageInformationIcon,
} from '@/shared/ui';
import type { SavedPresetRecord } from '../../model/types';
import { PresetForm } from '../PresetForm/PresetForm';

const Button = styled(SharedButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
}));

interface Props {
  preset: SavedPresetRecord;
  updatePreset: (
    id: string,
    options?: {
      name?: string;
      isDefault?: boolean;
      isFavorite?: boolean;
      updateContent?: boolean;
    },
  ) => Promise<void>;
  onClose: () => void;
  showUpdateContent?: boolean;
}

export function EditPresetDialog({
  preset,
  updatePreset,
  onClose,
  showUpdateContent = true,
}: Props) {
  const [name, setName] = useState(preset.name);
  const [isDefault, setIsDefault] = useState(preset.isDefault);
  const [isFavorite, setIsFavorite] = useState(preset.isFavorite);
  const [updateContent, setUpdateContent] = useState(false);
  const [nameError, setNameError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleNameChange = (value: string) => {
    setName(value);
    setNameError('');
  };

  const handleSave = () => {
    if (!name) {
      setNameError('Preset name is required');
      return;
    }

    setIsLoading(true);
    updatePreset(preset.id, { name, isDefault, isFavorite, updateContent })
      .then(onClose)
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  return (
    <Dialog
      open
      PaperProps={{
        sx: {
          borderRadius: 4,
          width: 470,
        },
      }}
      // needed to prevent apply on preset item in select
      onClick={(e) => e.stopPropagation()}
      // needed to prevent ripple effects on preset item in select
      onFocus={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          p: 2,
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            fontWeight: 500,
            fontSize: '1.5rem',
            lineHeight: '1.75rem',
            letterSpacing: 0,
          }}
        >
          Edit preset
        </DialogTitle>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        dividers
        sx={{
          p: 2,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <PresetForm
          name={name}
          isDefault={isDefault}
          isFavorite={isFavorite}
          nameError={nameError}
          onNameChange={handleNameChange}
          onIsDefaultChange={setIsDefault}
          onIsFavoriteChange={setIsFavorite}
        />
        {showUpdateContent && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1.5 }}
          >
            <LabeledSwitcher
              label="Replace preset content"
              value={updateContent}
              onChange={setUpdateContent}
            />
            <UsageInformationIcon
              sx={{ maxWidth: 200 }}
              placement="right"
              tooltipText="Replaces the saved preset configuration with your current filters and settings"
            />
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" color="primary" onClick={handleSave}>
            Save
          </Button>
          <Button variant="outlined" color="secondary" onClick={onClose}>
            Cancel
          </Button>
        </Box>
        <Loader active={isLoading} />
      </DialogContent>
    </Dialog>
  );
}
